import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

// Context
import { useLayoutContext } from '../../context/layout-context';

// Types
import { DropdownOption } from '../../../../../types';
import { SelectOptionsRequest, RequestStatus, SectionDashboardLayout } from '../../../../types';

// Icons
import IconChevronUp from '../../../../../components/icons/chevron-up';
import IconCheck from '../../../../../components/icons/check';
import IconChevronLeftThin from '../../../../../components/icons/chevron-left-thin';

// Components
import LoadingEllipsis from '../../../../components/loading-ellipsis';
import SearchInput from '../../../../components/search-input';
import SectionDashboardTooltip, { TooltipPopUpSide } from '../../../../components/section-dashboard-tooltip';

const StyledContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${props => props.theme.sectionDashboard.section.sectionMenu.select.textColor};

    & button {
        all: inherit;
        cursor: 'pointer';
    }
`;

const StyledLabel = styled.div`
    font-size: 0.9rem;
    font-weight: ${props => props.theme.sectionDashboard.section.sectionMenu.select.labelFontWeight};
    white-space: nowrap;
    text-overflow: ellipsis;
    color: ${props => props.theme.sectionDashboard.section.sectionMenu.select.labelColor};
    text-transform: uppercase;
`;

const StyledTriggerContent = styled.div<{ empty?: boolean }>`
    border: 2px solid ${props => props.theme.sectionDashboard.widget.menu.separatorColor};
    border-radius: 5px;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 150px;
    font-weight: 500;
    font-size: 0.9rem;
    cursor: ${props => (props.empty ? 'auto' : 'pointer')};
    white-space: nowrap;
    text-overflow: ellipsis;
    background-color: ${props => props.theme.sectionDashboard.widget.menu.triggerBackgroundColor};
`;

const StyledSubTriggerBase = css`
    height: 18px;
    display: flex;
    gap: 20px;
    align-items: center;
    padding: 20px 20px;
    outline: none;
    font-weight: 500;
`;

const StyledSubTrigger = styled(DropdownMenu.SubTrigger)`
    ${StyledSubTriggerBase}
    cursor: pointer;
    color: ${props => props.theme.sectionDashboard.widget.menu.textColor};

    &:hover {
        background-color: ${props => props.theme.sectionDashboard.widget.menu.backgroundHover};
    }
`;

const StyledOptionLabel = styled.div<{ empty?: boolean }>`
    font-size: 1rem;
    padding: 5px 10px;
    color: ${props => props.empty && props.theme.sectionDashboard.section.sectionMenu.select.optionLabelColorEmpty};
`;

const StyledIconContainer = styled.div`
    padding: 0px 10px;
    height: 100%;
    border-left: 1px solid ${props => props.theme.sectionDashboard.widget.menu.separatorColor};
`;

const StyledIconChevronSmall = styled(IconChevronUp)<{ empty?: boolean }>`
    transform: rotate(180deg);
    height: 14px;
    margin-top: 8px;
    width: auto;
    & path {
        fill: ${props =>
            props.empty
                ? props.theme.sectionDashboard.widget.menu.separatorColor
                : props.theme.sectionDashboard.widget.menu.textColor};
    }
`;

const StyledIconChevronLeftThin = styled(IconChevronLeftThin)`
    height: 18px;
    width: auto;
`;

const StyledContentBase = css`
    background-color: ${props => props.theme.sectionDashboard.section.sectionMenu.select.backgroundColor};
    min-width: 210px;
    padding: 0px 0px 10px 0px;
    border-radius: 6px;
    box-shadow: ${props => props.theme.sectionDashboard.widget.menu.shadow};
    max-height: 400px;
    color: ${props => props.theme.sectionDashboard.section.sectionMenu.select.textColor};
    overflow-y: auto;
`;

const StyledContent = styled(DropdownMenu.Content)<{ enableSearch: boolean }>`
    ${StyledContentBase}
    z-index: 21; // Overlap the top bar
    padding: ${props => !props.enableSearch && `15px 0px 10px 0px`};
`;

const StyledSubContent = styled(DropdownMenu.SubContent)<{ layout: SectionDashboardLayout; enableSearch: boolean }>`
    ${StyledContentBase}
    z-index: 51; // Overlap the sidebar
    max-width: ${props =>
        (props.layout === SectionDashboardLayout.SM || props.layout === SectionDashboardLayout.XS) && '300px'};
    padding: ${props => !props.enableSearch && `15px 0px 10px 0px`};
`;

const StyledSearchContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    position: sticky;
    top: 0;
    background-color: ${props => props.theme.sectionDashboard.section.sectionMenu.select.backgroundColor};
    z-index: 1;
    padding: 10px 2px 0px 2px;
    box-shadow: ${props => props.theme.sectionDashboard.widget.menu.shadow};
    margin-bottom: 10px;
`;

const StyledDropdownItem = styled(DropdownMenu.Item)`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px 10px 15px;
    cursor: pointer;
    word-break: break-all;

    &:hover {
        background-color: ${props => props.theme.sectionDashboard.widget.menu.backgroundHover};
        border: none;
        outline: none;
    }
`;

const StyledIconCheck = styled(IconCheck)<{ visible: boolean }>`
    width: 18px;
    visibility: ${props => (props.visible ? 'visible' : 'hidden')};
`;

const StyledLoadingContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StyledDropdownSkeleton = styled.div<{
    responsive: boolean;
    viewportSize: SectionDashboardLayout;
    smallViewports: SectionDashboardLayout[];
}>`
    height: 19px;
    width: ${props => (props.responsive && props.smallViewports.includes(props.viewportSize) ? '20px' : '150px')};
    background: ${props => props.theme.sectionDashboard.section.sectionMenu.select.skeletonColor};
    border-radius: ${props => (props.responsive && props.smallViewports.includes(props.viewportSize) ? '10px' : '5px')};
`;

export type WidgetSectionSelectInfiniteProps = {
    label: string;
    allLabel: string;
    dropdownOptions: SelectOptionsRequest;
    selectedOption?: DropdownOption;
    onOptionChange: (option: string) => void;
    emptyMessage: string;
    customIcon?: React.ReactNode;
    responsive?: boolean;
    menuContentAlign?: 'start' | 'end';
    fetchNextPage?: () => void;
    isFetchingNextPage?: boolean;
    enableSearch?: boolean;
    searchText?: string;
    handleSearch?: (search: string) => void;
};

const WidgetSectionSelectInfinite = ({
    label,
    allLabel,
    dropdownOptions,
    selectedOption,
    onOptionChange,
    emptyMessage,
    responsive = true,
    menuContentAlign = 'end',
    fetchNextPage,
    isFetchingNextPage,
    enableSearch = true,
    searchText,
    handleSearch,
}: WidgetSectionSelectInfiniteProps) => {
    let { layout } = useLayoutContext();

    const largeViewports = [SectionDashboardLayout.XL, SectionDashboardLayout.LG];
    const smallViewports = [SectionDashboardLayout.MD, SectionDashboardLayout.SM, SectionDashboardLayout.XS];

    const dropdownRef = useRef<HTMLInputElement>(null);

    const [isListLoading, setIsListLoading] = useState(false);

    const handleScrollEnd = () => {
        if (dropdownRef.current) {
            const element = dropdownRef.current as HTMLElement;

            if (element.scrollHeight - element.scrollTop === element.clientHeight) {
                fetchNextPage && fetchNextPage();
            }
        }
    };

    useEffect(() => {
        setIsListLoading(false);
    }, [dropdownOptions]);

    const renderTrigger = ({ isEmpty }: { isEmpty: boolean }) => {
        return (
            <>
                {(layout === SectionDashboardLayout.XL || !responsive) && <StyledLabel>{label}:</StyledLabel>}
                {(!responsive || largeViewports.includes(layout)) && (
                    <DropdownMenu.Trigger>
                        <StyledTriggerContent empty={isEmpty}>
                            <StyledOptionLabel empty={isEmpty}>
                                {selectedOption
                                    ? selectedOption.label.length > 20
                                        ? selectedOption.label.substring(0, 20) + '...'
                                        : selectedOption.label
                                    : emptyMessage}
                            </StyledOptionLabel>
                            <StyledIconContainer>
                                <StyledIconChevronSmall empty={isEmpty} />
                            </StyledIconContainer>
                        </StyledTriggerContent>
                    </DropdownMenu.Trigger>
                )}
            </>
        );
    };

    if (dropdownOptions.status === RequestStatus.LOADING) {
        return (
            <StyledLoadingContainer>
                {(!responsive || layout === SectionDashboardLayout.XL) && <StyledLabel>{label}:</StyledLabel>}
                <StyledDropdownSkeleton responsive={responsive} viewportSize={layout} smallViewports={smallViewports} />
            </StyledLoadingContainer>
        );
    }

    if (
        (dropdownOptions.status === RequestStatus.SUCCESS && selectedOption) ||
        dropdownOptions.status === RequestStatus.EMPTY ||
        dropdownOptions.status === RequestStatus.ERROR
    ) {
        if (responsive && smallViewports.includes(layout)) {
            return (
                <DropdownMenu.Sub>
                    <StyledSubTrigger>
                        <StyledIconChevronLeftThin />
                        {label}
                    </StyledSubTrigger>
                    <DropdownMenu.Portal>
                        <StyledSubContent
                            layout={layout}
                            onScroll={handleScrollEnd}
                            ref={dropdownRef}
                            enableSearch={enableSearch}
                        >
                            {enableSearch && (
                                <StyledSearchContainer>
                                    <SearchInput
                                        value={searchText}
                                        placeholder={`Search ${allLabel}`}
                                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                            handleSearch && handleSearch(event.currentTarget.value);
                                            setIsListLoading(true);
                                        }}
                                    />
                                </StyledSearchContainer>
                            )}

                            {isListLoading ? (
                                <LoadingEllipsis />
                            ) : dropdownOptions.status === RequestStatus.SUCCESS && selectedOption ? (
                                dropdownOptions.options.map(option => {
                                    return (
                                        <StyledDropdownItem
                                            onClick={() => onOptionChange(option.value)}
                                            key={option.value}
                                        >
                                            <StyledIconCheck visible={option.value === selectedOption.value} />
                                            <SectionDashboardTooltip
                                                copy={option.label}
                                                usePlaceholder={false}
                                                tooltipPopUpSide={'right' as TooltipPopUpSide | undefined}
                                                contentWidth={400}
                                            >
                                                {option.label.length > 40
                                                    ? option.label.substring(0, 40) + '...'
                                                    : option.label}
                                            </SectionDashboardTooltip>
                                        </StyledDropdownItem>
                                    );
                                })
                            ) : (
                                <StyledDropdownItem>
                                    <StyledOptionLabel empty={true}>{emptyMessage}</StyledOptionLabel>
                                </StyledDropdownItem>
                            )}

                            {!isListLoading && isFetchingNextPage && <LoadingEllipsis />}
                        </StyledSubContent>
                    </DropdownMenu.Portal>
                </DropdownMenu.Sub>
            );
        }

        return (
            <StyledContainer>
                <DropdownMenu.Root>
                    {renderTrigger({ isEmpty: false })}

                    <DropdownMenu.Portal>
                        <StyledContent
                            side="bottom"
                            align={menuContentAlign}
                            onScroll={handleScrollEnd}
                            ref={dropdownRef}
                            enableSearch={enableSearch}
                        >
                            {enableSearch && (
                                <StyledSearchContainer>
                                    <SearchInput
                                        value={searchText}
                                        placeholder={`Search ${allLabel}`}
                                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                            handleSearch && handleSearch(event.currentTarget.value);
                                            setIsListLoading(true);
                                        }}
                                    />
                                </StyledSearchContainer>
                            )}

                            {isListLoading ? (
                                <LoadingEllipsis />
                            ) : dropdownOptions.status === RequestStatus.SUCCESS && selectedOption ? (
                                dropdownOptions.options.map(option => {
                                    return (
                                        <StyledDropdownItem
                                            onClick={() => onOptionChange(option.value)}
                                            key={option.value}
                                        >
                                            <StyledIconCheck visible={option.value === selectedOption.value} />
                                            <SectionDashboardTooltip
                                                copy={option.label}
                                                usePlaceholder={false}
                                                tooltipPopUpSide={'right' as TooltipPopUpSide | undefined}
                                                contentWidth={400}
                                            >
                                                {option.label.length > 40
                                                    ? option.label.substring(0, 40) + '...'
                                                    : option.label}
                                            </SectionDashboardTooltip>
                                        </StyledDropdownItem>
                                    );
                                })
                            ) : (
                                <StyledDropdownItem>
                                    <StyledOptionLabel empty={true}>{emptyMessage}</StyledOptionLabel>
                                </StyledDropdownItem>
                            )}

                            {!isListLoading && isFetchingNextPage && <LoadingEllipsis />}
                        </StyledContent>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            </StyledContainer>
        );
    }

    return null;
};

export default WidgetSectionSelectInfinite;
